import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Drawer, Divider, Table, Tabs } from 'antd';
import PropTypes from 'prop-types';

import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';

import WorkflowConfigureSourceSelector from './selectors/WorkflowConfigureSourceSelector';

import { getSourceFieldMap, getType } from './workflowHelpers';
import { CARD_DATA } from './workflow.constants';
import { resolveFormDivisionIds } from '../formHelpers';

const { TabPane } = Tabs;

const getDivisionCustomFieldSections = (customFields, divisionId) => {
  const {
    [divisionId]: { fields = [] } = {},
  } = customFields;
  return fields.filter((section) => section.id !== 'timecard');
};

const flattenCustomFieldSections = (sections = []) => {
  const fields = [];
  sections.forEach((section) => {
    const { fields: sectionFields = [], name: sectionName } = section;
    sectionFields.forEach((field) => {
      const {
        configProps: {
          title: fieldTitle,
        } = {},
      } = field;
      fields.push({
        ...field,
        title: `${sectionName} - ${fieldTitle}`,
      });
    });
  });
  return fields;
};

export default function WorkflowTimeConfigureDrawer({
  visible,
  onClose,
  onSubmit,
  sourceName,
  sourceSections = [],
  fieldMappings = {},
  onFieldMappingsChange,
  divisionId,
  divisionIds = [],
  customFields = {},
}) {
  const divisionMap = useSelector((state) => state.settings.divisions);
  const resolvedDivisionIds = useMemo(() => (
    resolveFormDivisionIds({ divisionIds, divisionId })
  ), [divisionIds, divisionId]);

  const showPerDivision = resolvedDivisionIds.length > 1;
  const [selectedDivisionId, setSelectedDivisionId] = useState(resolvedDivisionIds[0] ?? null);

  useEffect(() => {
    if (visible && !selectedDivisionId && resolvedDivisionIds.length) {
      setSelectedDivisionId(resolvedDivisionIds[0]);
    }
  }, [visible, selectedDivisionId, resolvedDivisionIds]);

  const flattenedTargetFields = useMemo(() => {
    if (showPerDivision && selectedDivisionId) {
      return flattenCustomFieldSections(
        getDivisionCustomFieldSections(customFields, selectedDivisionId),
      );
    }
    const sections = resolvedDivisionIds.flatMap((divId) => (
      getDivisionCustomFieldSections(customFields, divId)
    ));
    return flattenCustomFieldSections(sections);
  }, [customFields, resolvedDivisionIds, selectedDivisionId, showPerDivision]);

  const sourceFieldMap = useMemo(() => getSourceFieldMap(
    [CARD_DATA].concat(sourceSections),
  ), [sourceSections]);

  const columns = useMemo(() => ([
    {
      dataIndex: 'sourceField',
      title: 'Source Field',
      width: 425,
      render: (_, record) => {
        const {
          id: targetId,
        } = record;
        const type = getType(record);
        let {
          [type]: eligibleSourceFields = [],
        } = sourceFieldMap;

        // Allow mapping of static text fields to text input fields
        if (type === 'text') {
          const staticTextFields = sourceFieldMap.staticText ?? [];
          eligibleSourceFields = eligibleSourceFields.concat(staticTextFields);
        }

        const relevantFieldMappings = fieldMappings?.version === 'v2'
          ? (fieldMappings[selectedDivisionId] ?? {})
          : fieldMappings;

        const {
          [targetId]: defaultValue,
        } = relevantFieldMappings ?? {};

        return (
          <WorkflowConfigureSourceSelector
            fieldMappings={relevantFieldMappings}
            defaultValue={defaultValue}
            onFieldMappingsChange={(newMappings) => {
              onFieldMappingsChange(newMappings, selectedDivisionId);
            }}
            targetId={targetId}
            eligibleSourceFields={eligibleSourceFields}
            formId={selectedDivisionId}
            value={defaultValue}
          />
        );
      },
    },
    {
      dataIndex: 'title',
      title: 'Target Field',
    },
  ]), [
    sourceFieldMap,
    fieldMappings,
    selectedDivisionId,
    onFieldMappingsChange,
  ]);

  const mappingTable = (
    <Table
      dataSource={flattenedTargetFields}
      columns={columns}
      pagination={false}
      size="small"
      scroll={{ y: 'calc(100vh - 218px)' }}
      rowKey="id"
    />
  );

  return (
    <Drawer
      title="Configure Field Mapping"
      width={1000}
      onClose={onClose}
      visible={visible}
    >
      <span>
        Configure which fields from this <b>{sourceName}</b> form are loaded into the custom timecard fields
        {showPerDivision ? ' for the selected division' : ''}.
      </span>
      <Divider style={{ marginBottom: 0 }} />
      {showPerDivision ? (
        <Tabs
          tabPosition="left"
          activeKey={selectedDivisionId}
          onChange={setSelectedDivisionId}
          destroyInactiveTabPane
          style={{ height: '100%' }}
        >
          {resolvedDivisionIds.map((divId) => (
            <TabPane
              tab={(
                <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {divisionMap[divId]?.name ?? divId}
                </div>
              )}
              key={divId}
            >
              {mappingTable}
            </TabPane>
          ))}
        </Tabs>
      ) : mappingTable}
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </Drawer>
  );
}

WorkflowTimeConfigureDrawer.propTypes = {
  customFields: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  divisionId: PropTypes.string,
  divisionIds: PropTypes.arrayOf(PropTypes.string),
  fieldMappings: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onClose: PropTypes.func.isRequired,
  onFieldMappingsChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  sourceName: PropTypes.string,
  sourceSections: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  visible: PropTypes.bool,
};

WorkflowTimeConfigureDrawer.defaultProps = {
  customFields: {},
  divisionId: null,
  divisionIds: [],
  fieldMappings: {},
  sourceName: '',
  sourceSections: [],
  visible: false,
};
